import {
  defaultBuilderModel,
  extractAgentChanges,
  lineDiff,
  type AgentChanges,
  type ChatMessage,
  type ProviderDto,
  type SessionLevel,
} from '@agent-platform/shared';
import {
  ArrowUp,
  Check,
  CircleAlert,
  LoaderCircle,
  RotateCcw,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/components/markdown';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { errorMessage } from '@/lib/api';
import { usePreferences } from '@/lib/preferences';
import { usePaged } from '@/lib/queries';
import { postEventStream } from '@/lib/sse';
import { cn } from '@/lib/utils';

/** What the builder may attach, by name, and what is attached now. */
export interface BuilderResources {
  tools: string[];
  mcpServers: string[];
  skills: string[];
  workflows: string[];
  /** Claude Code built-in tools (the server's allowlist, or the ones turned on). */
  claudeBuiltinTools: string[];
}

/** The parts of the draft a change card compares against. */
export interface BuilderDraft {
  systemPrompt: string;
  /** The agent's connection, by name. */
  provider: string | null;
  model: string;
  attached: BuilderResources;
  memoryMode: 'stateless' | 'stateful' | 'sessions';
  lastMessages: string;
  sessionLevels: SessionLevel[];
  maxSteps: string;
  maxDurationSeconds: string;
}

interface Turn {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  status?: 'streaming' | 'done' | 'failed';
  error?: string;
  decision?: 'applied' | 'discarded';
  skipped?: string[];
}

/** Stands in for the change card while the builder is still writing its proposal. */
function PreparingChanges() {
  return (
    <div
      className="bg-card shadow-panel overflow-hidden rounded-xl"
      role="status"
      data-testid="builder-changes-pending"
    >
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <LoaderCircle className="text-tint-violet size-4 shrink-0 animate-spin" aria-hidden />
        <span className="text-sm font-medium">Preparing proposed changes…</span>
      </div>
      <div className="grid gap-2 px-4 py-3" aria-hidden>
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-3/5" />
      </div>
    </div>
  );
}

/** Everything the builder may use, by name. */
export interface BuilderAvailable extends BuilderResources {
  /** Connection names. */
  providers: string[];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One proposed change set, as rows the user can read before applying. */
function ChangeCard({
  changes,
  draft,
  available,
  decision,
  skipped,
  onApply,
  onDiscard,
  disabled,
}: {
  changes: AgentChanges;
  draft: BuilderDraft;
  available: BuilderAvailable;
  decision?: 'applied' | 'discarded';
  skipped?: string[];
  onApply: () => void;
  onDiscard: () => void;
  disabled: boolean;
}) {
  const [showDiff, setShowDiff] = useState(false);
  // The draft as it was when this proposal arrived, so the card still reads right after applying.
  const [base] = useState(draft);
  draft = base;
  const rows: Array<{ label: string; value: React.ReactNode }> = [];
  const diff = useMemo(
    () =>
      changes.systemPrompt !== undefined ? lineDiff(draft.systemPrompt, changes.systemPrompt) : [],
    [changes.systemPrompt, draft.systemPrompt],
  );
  if (changes.systemPrompt !== undefined) {
    const added = diff.filter((l) => l.kind === 'added').length;
    const removed = diff.filter((l) => l.kind === 'removed').length;
    rows.push({
      label: 'System prompt',
      value: (
        <span className="flex flex-wrap items-center gap-2">
          {draft.systemPrompt.trim() ? 'Rewritten' : 'Written'}
          <span className="text-muted-foreground text-xs">
            +{added} −{removed} lines
          </span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs underline"
            onClick={() => setShowDiff((v) => !v)}
            aria-expanded={showDiff}
          >
            {showDiff ? 'Hide changes' : 'Show changes'}
          </button>
        </span>
      ),
    });
  }
  if (changes.provider && changes.provider !== draft.provider)
    rows.push({
      label: 'Connection',
      value: (
        <span className="flex flex-wrap items-center gap-2">
          {`${draft.provider ?? 'none'} → ${changes.provider}`}
          {!available.providers.includes(changes.provider) && (
            <Badge variant="destructive" title="Does not exist; it will be skipped">
              not found
            </Badge>
          )}
        </span>
      ),
    });
  if (changes.model && changes.model !== draft.model)
    rows.push({ label: 'Model', value: `${draft.model || 'none'} → ${changes.model}` });
  const attach = (
    label: string,
    key: keyof BuilderResources,
    change: { add: string[]; remove: string[] } | undefined,
  ) => {
    if (!change) return;
    // Only real changes: not re-adding what is attached or removing what isn't.
    const add = change.add.filter((n) => !draft.attached[key].includes(n));
    const remove = change.remove.filter((n) => draft.attached[key].includes(n));
    if (add.length === 0 && remove.length === 0) return;
    rows.push({
      label,
      value: (
        <span className="flex flex-wrap gap-1">
          {add.map((n) => (
            <Badge
              key={`+${n}`}
              variant={available[key].includes(n) ? 'secondary' : 'destructive'}
              className="font-mono font-normal"
              title={available[key].includes(n) ? undefined : 'Does not exist; it will be skipped'}
            >
              + {n}
            </Badge>
          ))}
          {remove.map((n) => (
            <Badge key={`-${n}`} variant="outline" className="font-mono font-normal line-through">
              {n}
            </Badge>
          ))}
        </span>
      ),
    });
  };
  attach('Tools', 'tools', changes.tools);
  attach('MCP servers', 'mcpServers', changes.mcpServers);
  attach('Skills', 'skills', changes.skills);
  attach('Workflows', 'workflows', changes.workflows);
  attach('Claude built-in tools', 'claudeBuiltinTools', changes.claudeBuiltinTools);
  const levels = changes.memory?.sessionLevels;
  if (
    changes.memory &&
    (changes.memory.mode !== draft.memoryMode ||
      (changes.memory.lastMessages !== undefined &&
        String(changes.memory.lastMessages) !== draft.lastMessages) ||
      (levels !== undefined && JSON.stringify(levels) !== JSON.stringify(draft.sessionLevels)))
  )
    rows.push({
      label: 'Memory',
      value:
        changes.memory.mode === 'stateful'
          ? `On, last ${changes.memory.lastMessages ?? (draft.lastMessages || 20)} messages`
          : changes.memory.mode === 'sessions'
            ? `Session levels: ${(levels ?? draft.sessionLevels).map((l) => l.name).join(' → ') || 'none yet'}`
            : 'Off',
    });
  if (changes.limits) {
    const l = changes.limits;
    const value = [
      l.maxSteps !== undefined &&
        String(l.maxSteps) !== draft.maxSteps &&
        `${draft.maxSteps} → ${plural(l.maxSteps, 'step')}`,
      l.maxDurationSeconds !== undefined &&
        String(l.maxDurationSeconds) !== draft.maxDurationSeconds &&
        `${draft.maxDurationSeconds} s → ${l.maxDurationSeconds} s`,
      l.maxOutputTokens !== undefined && `max ${l.maxOutputTokens} output tokens`,
    ]
      .filter(Boolean)
      .join(', ');
    if (value) rows.push({ label: 'Limits', value });
  }
  if (changes.params) {
    const p = changes.params;
    rows.push({
      label: 'Model parameters',
      value: Object.entries(p)
        .map(([k, v]) => `${k} ${v}`)
        .join(', '),
    });
  }

  return (
    <div className="bg-card shadow-panel overflow-hidden rounded-xl" data-testid="builder-changes">
      <div className="flex items-start gap-2 border-b px-4 py-3">
        <Sparkles className="text-tint-violet mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="grid gap-0.5">
          <span className="text-sm font-medium">Proposed changes</span>
          {changes.summary && (
            <span className="text-muted-foreground text-xs">{changes.summary}</span>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground px-4 py-3 text-sm">No changes in this proposal.</p>
      ) : (
        <dl className="divide-y text-sm">
          {rows.map((r) => (
            <div key={r.label} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-3 px-4 py-2.5">
              <dt className="text-muted-foreground">{r.label}</dt>
              <dd className="min-w-0">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {showDiff && (
        <pre className="bg-muted max-h-72 overflow-auto border-t p-3 font-mono text-xs leading-5 whitespace-pre-wrap [overflow-wrap:anywhere]">
          {diff.map((line, i) => (
            <div
              key={i}
              className={
                line.kind === 'added'
                  ? 'bg-green-500/15 text-green-800 dark:text-green-300'
                  : line.kind === 'removed'
                    ? 'bg-red-500/15 text-red-800 line-through dark:text-red-300'
                    : 'text-muted-foreground'
              }
            >
              {line.kind === 'added' ? '+ ' : line.kind === 'removed' ? '- ' : '  '}
              {line.text || ' '}
            </div>
          ))}
        </pre>
      )}
      <div className="bg-muted/40 flex flex-wrap items-center gap-2 border-t px-4 py-2.5">
        {decision === 'applied' ? (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <Check className="size-4 text-green-600" aria-hidden />
            Applied to the draft
            {skipped && skipped.length > 0 && (
              <span className="text-muted-foreground text-xs">
                (skipped {skipped.join(', ')}: not found)
              </span>
            )}
          </span>
        ) : decision === 'discarded' ? (
          <span className="text-muted-foreground text-sm">Discarded</span>
        ) : (
          <>
            <Button size="sm" onClick={onApply} disabled={disabled || rows.length === 0}>
              <Check aria-hidden />
              Apply to draft
            </Button>
            <Button size="sm" variant="ghost" onClick={onDiscard} disabled={disabled}>
              <X aria-hidden />
              Discard
            </Button>
            <span className="text-muted-foreground ml-auto text-xs">
              Nothing changes until you apply. Publishing stays separate.
            </span>
          </>
        )}
      </div>
    </div>
  );
}

const SUGGESTIONS = [
  'Help me set this agent up for my use case',
  'Review this agent and suggest improvements',
  'Which tools and skills should this agent use?',
];

/**
 * Build mode: a chat with an assistant that understands the use case and proposes changes to this
 * agent's draft. Proposals are shown as cards; the caller applies them.
 */
export function AgentBuilder({
  tenantId,
  agentId,
  agentName,
  draft,
  available,
  onApply,
  className,
}: {
  tenantId: string;
  agentId: string;
  agentName: string;
  draft: BuilderDraft;
  available: BuilderAvailable;
  /** Applies a proposal to the draft; returns the names that did not exist and were skipped. */
  onApply: (changes: AgentChanges) => string[];
  className?: string;
}) {
  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers');
  const providerList = useMemo(
    () => providers.data?.pages.flatMap((p) => p.items) ?? [],
    [providers.data],
  );
  const [choice, setChoice] = useState<{ providerId: string; model: string } | null>(null);
  // Unless the user picks another model here, the builder runs on Local Claude when it works.
  const selected = choice ?? defaultBuilderModel(providerList);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const nextId = useRef(1);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { sendWith } = usePreferences();

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns]);
  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // A chat mounted while hidden measures 0; keep at least one comfortable line.
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 36), 192)}px`;
  }, [input]);

  const update = (id: number, fn: (t: Turn) => Turn) =>
    setTurns((all) => all.map((t) => (t.id === id ? fn(t) : t)));

  const pickModel = (value: string) => {
    const [providerId, ...rest] = value.split('::');
    const next = { providerId: providerId!, model: rest.join('::') };
    // The select can report an empty value on its own (e.g. while its items mount): keep the model.
    if (!next.providerId || !next.model) return;
    setChoice(next);
  };

  async function send(text = input) {
    const content = text.trim();
    if (!content || running || !selected) return;
    // The builder is stateless: send the conversation so far (with its earlier proposals).
    const history: ChatMessage[] = turns
      .filter((t) => t.status !== 'failed' && t.content)
      .map((t) => ({ role: t.role, content: t.content }));
    const userTurn: Turn = { id: nextId.current++, role: 'user', content };
    const replyId = nextId.current++;
    setTurns((all) => [
      ...all,
      userTurn,
      { id: replyId, role: 'assistant', content: '', status: 'streaming' },
    ]);
    setInput('');
    setRunning(true);
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await postEventStream(
        `/agents/${agentId}/builder`,
        {
          providerConnectionId: selected.providerId,
          model: selected.model,
          messages: [...history, { role: 'user', content }],
        },
        (event) => {
          const e = event as { type: string; text?: string; message?: string };
          if (e.type === 'text.delta')
            update(replyId, (t) => ({ ...t, content: t.content + e.text }));
          else if (e.type === 'builder.error')
            update(replyId, (t) => ({ ...t, status: 'failed', error: e.message }));
          else if (e.type === 'builder.done')
            update(replyId, (t) => (t.status === 'failed' ? t : { ...t, status: 'done' }));
        },
        abort.signal,
      );
    } catch (error) {
      update(replyId, (t) => ({ ...t, status: 'failed', error: errorMessage(error) }));
    } finally {
      update(replyId, (t) =>
        t.status === 'streaming'
          ? {
              ...t,
              status: abort.signal.aborted ? 'failed' : 'done',
              ...(abort.signal.aborted ? { error: 'Stopped' } : {}),
            }
          : t,
      );
      setRunning(false);
      abortRef.current = null;
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const mod = e.metaKey || e.ctrlKey;
    if (sendWith === 'enter' ? !mod : mod) {
      e.preventDefault();
      void send();
    }
  };

  const empty = turns.length === 0;
  const modelPicker = (
    <Select
      value={selected ? `${selected.providerId}::${selected.model}` : ''}
      onValueChange={pickModel}
    >
      <SelectTrigger
        size="sm"
        aria-label="Builder model"
        // A quiet chip inside the composer, like the model switch in chat apps.
        className="hover:bg-accent dark:hover:bg-accent h-9 max-w-32 rounded-full border-0 bg-transparent text-xs shadow-none sm:max-w-56 dark:bg-transparent"
      >
        {/* Shown by name: the saved model may not be in the provider's listed models. */}
        <SelectValue placeholder="Choose a model">{selected?.model}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {providerList.map((p) => (
          <SelectGroup key={p.id}>
            <SelectLabel>{p.name}</SelectLabel>
            {[...new Set([...(p.models ?? []), ...(p.defaultModel ? [p.defaultModel] : [])])].map(
              (m) => (
                <SelectItem key={`${p.id}::${m}`} value={`${p.id}::${m}`}>
                  {m}
                </SelectItem>
              ),
            )}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );

  const composer = (
    <form
      className="bg-border/80 focus-within:bg-ring/60 w-full rounded-[27px] p-px transition-colors"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <div className="bg-card rounded-[26px]">
        <label htmlFor="builder-input" className="sr-only">
          Describe what the agent should do
        </label>
        {/* One row: text · model · actions; it grows upward with the text. */}
        <div className="flex items-end gap-1 p-2">
          <textarea
            id="builder-input"
            ref={inputRef}
            rows={1}
            placeholder="Describe your use case, or what to change…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            className="placeholder:text-muted-foreground block max-h-48 min-w-0 flex-1 resize-none bg-transparent py-2 pr-1 pl-3 text-sm leading-5 outline-none placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap"
          />
          <div className="shrink-0">{modelPicker}</div>
          {!empty && (
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              className="text-muted-foreground hover:text-foreground rounded-full"
              aria-label="New build chat"
              title="New build chat"
              onClick={() => setTurns([])}
              disabled={running}
            >
              <RotateCcw aria-hidden />
            </Button>
          )}
          {running ? (
            <Button
              type="button"
              size="icon-lg"
              variant="outline"
              className="rounded-full"
              aria-label="Stop"
              onClick={() => abortRef.current?.abort()}
            >
              <Square className="size-3 fill-current" aria-hidden />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon-lg"
              className="rounded-full"
              aria-label="Send to builder"
              disabled={!input.trim() || !selected}
            >
              <ArrowUp aria-hidden />
            </Button>
          )}
        </div>
      </div>
    </form>
  );

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div
        className={cn('min-h-0 flex-1 overflow-y-auto px-4', empty && 'flex flex-col')}
        role="log"
        aria-label="Builder conversation"
        aria-live="polite"
      >
        {empty ? (
          <div className="m-auto grid w-full max-w-3xl justify-items-center gap-5 py-10">
            <div className="bg-muted flex size-10 items-center justify-center rounded-full border">
              <Sparkles className="text-tint-violet size-5" aria-hidden />
            </div>
            <div className="grid gap-1 text-center">
              <p className="text-muted-foreground text-xl">
                What should <span className="text-foreground font-medium">{agentName}</span> do?
              </p>
              <p className="text-muted-foreground text-sm">
                Describe the use case. The builder asks what it needs, then proposes the prompt,
                tools, skills and settings for you to apply.
              </p>
            </div>
            {composer}
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <Button
                  key={s}
                  variant="outline"
                  size="sm"
                  disabled={!selected}
                  onClick={() => void send(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
            {!providers.isPending && !selected && (
              <p className="text-destructive flex items-center gap-1.5 text-xs">
                <CircleAlert className="size-3.5" aria-hidden />
                Add a model connection in LLM setup to use the builder.
              </p>
            )}
          </div>
        ) : (
          <div className="mx-auto grid w-full max-w-3xl gap-6 py-6">
            {turns.map((t) => {
              if (t.role === 'user')
                return (
                  <div
                    key={t.id}
                    className="bg-accent max-w-[85%] justify-self-end rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap"
                  >
                    {t.content}
                  </div>
                );
              const { text, changes, error, pending } = extractAgentChanges(t.content);
              return (
                <div key={t.id} className="grid gap-3" data-testid="builder-reply">
                  <div>
                    {text ? (
                      <Markdown>{text}</Markdown>
                    ) : (
                      t.status === 'streaming' && (
                        <span className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                          <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
                          Thinking…
                        </span>
                      )
                    )}
                  </div>
                  {pending && t.status === 'streaming' && <PreparingChanges />}
                  {changes && (
                    <ChangeCard
                      changes={changes}
                      draft={draft}
                      available={available}
                      decision={t.decision}
                      skipped={t.skipped}
                      disabled={running}
                      onApply={() => {
                        const skipped = onApply(changes);
                        update(t.id, (x) => ({ ...x, decision: 'applied', skipped }));
                      }}
                      onDiscard={() => update(t.id, (x) => ({ ...x, decision: 'discarded' }))}
                    />
                  )}
                  {error && t.status !== 'streaming' && (
                    <p className="text-destructive text-sm" role="alert">
                      {error} Ask the builder to try again.
                    </p>
                  )}
                  {t.error && (
                    <p className="text-destructive text-sm" role="alert">
                      {t.error}
                    </p>
                  )}
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>
      {!empty && <div className="mx-auto w-full max-w-3xl px-4 pt-2 pb-4">{composer}</div>}
    </div>
  );
}
